import { useState } from 'react';
import { IconArrowsVertical } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import { NSTable } from 'bricks';
import PeriodCellValue from 'views/ProFormaTable/components/ProForma/ProFormaDevelopment/DevelopmentUsesTable/PeriodCellValue/PeriodCellValue';
import { IExtractedUse } from 'types/db/ExtractedProForma';
import { IFormattedCashFlowPeriod } from 'views/ProFormaTable/types';
import {
    AMOUNT_COLUMN, TOTAL_LABEL, TOTAL_PERIOD_CELL_CLASS_NAME,
} from '../constants';
import {
    formatAmount, periodValues, sumPeriodValues, sumValues, toNumber,
} from '../helpers';
import PeriodHeaderCells from '../PeriodHeaderCells/PeriodHeaderCells';
import UsesBucketRows from './UsesBucketRows/UsesBucketRows';

export interface UsesTableProps {
    uses: IExtractedUse[];
    periodColumns: IFormattedCashFlowPeriod[];
}

const UsesTable = ({ uses, periodColumns }: UsesTableProps) => {
    const [collapsedBuckets, setCollapsedBuckets] = useState<Set<number>>(new Set());
    const toggleBucket = (index: number) => setCollapsedBuckets((previous) => {
        const next = new Set(previous);
        if (!next.delete(index)) next.add(index);
        return next;
    });
    const hasCollapsedBuckets = collapsedBuckets.size > 0;
    const toggleAllBuckets = () => setCollapsedBuckets(hasCollapsedBuckets ? new Set() : new Set(uses.map((_, index) => index)));

    const budgetLines = uses.flatMap(use => use.budgetLines);
    const grandTotal = sumValues(budgetLines.map(line => toNumber(line.total)));
    const grandPeriodTotals = sumPeriodValues(budgetLines.map(line => periodValues(line.periods)));

    return (
        <div className="d-flex scrollable-container pb-1">
            <NSTable className="NSTable--sticky-headers NSTable--sticky-headers--full-width NSTable--sticky-headers--two-sticky-columns">
                <thead className="NSTable__thead">
                    <tr className="NSTable__thead__tr">
                        <th className="NSTable__thead__tr__th NSTable__thead__tr__th--right-border align-middle py-1">
                            <div className="d-flex align-items-center">
                                <button
                                    type="button"
                                    className="bg-transparent border-0 p-0 mr-2 text-muted cursor--pointer"
                                    aria-label={hasCollapsedBuckets ? 'Expand all uses' : 'Collapse all uses'}
                                    onClick={toggleAllBuckets}
                                >
                                    <NSIcon icon={IconArrowsVertical} />
                                </button>
                                <span className="ml-0">Uses</span>
                            </div>
                        </th>
                        <th className="NSTable__thead__tr__th NSTable__thead__tr__th--right-border align-middle text-right py-1">{AMOUNT_COLUMN}</th>
                        <PeriodHeaderCells periodColumns={periodColumns} />
                    </tr>
                </thead>
                <tbody className="NSTable__tbody">
                    {uses.map((use, index) => (
                        <UsesBucketRows
                            key={`${use.name}-${index}`}
                            use={use}
                            periodColumns={periodColumns}
                            isExpanded={!collapsedBuckets.has(index)}
                            onToggle={() => toggleBucket(index)}
                        />
                    ))}
                </tbody>
                <tfoot className="NSTable__tfoot font-weight-bold">
                    <tr className="NSTable__tfoot__tr">
                        <td className="NSTable__tfoot__tr__td NSTable__tfoot__tr__td--right-border text-left">{TOTAL_LABEL}</td>
                        <td className="NSTable__tfoot__tr__td NSTable__tfoot__tr__td--right-border text-right text-nowrap pr-2">
                            {formatAmount(grandTotal)}
                        </td>
                        {periodColumns.map(column => (
                            <PeriodCellValue
                                key={column.value}
                                period={column}
                                value={formatAmount(grandPeriodTotals.get(column.period) ?? null)}
                                cellClassName={TOTAL_PERIOD_CELL_CLASS_NAME}
                            />
                        ))}
                    </tr>
                </tfoot>
            </NSTable>
        </div>
    );
};

export default UsesTable;
